// src/views/Shipments.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FaSync, FaBan, FaTimes } from 'react-icons/fa';
import './Shipments.css';

const API = import.meta.env.VITE_API_URL;

/** Build a public tracking URL for a known carrier; fall back to 17track if unknown. */
function buildTrackingUrl(rawCarrier, tracking) {
  const tn = String(tracking || '').trim();
  if (!tn) return null;

  const norm = String(rawCarrier || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  const is = (needle) => norm.includes(needle);

  if (is('ups')) {
    return `https://www.ups.com/track?loc=en_US&tracknum=${encodeURIComponent(tn)}`;
  }
  if (is('fedex') || is('fx')) {
    return `https://www.fedex.com/fedextrack/?trknbr=${encodeURIComponent(tn)}`;
  }
  if (is('usps') || is('postal service') || is('post office')) {
    return `https://tools.usps.com/go/TrackConfirmAction?qtc_tLabels1=${encodeURIComponent(tn)}`;
  }
  if (is('dhl') && !is('ecommerce')) {
    return `https://www.dhl.com/global-en/home/tracking/tracking-express.html?tracking-id=${encodeURIComponent(tn)}`;
  }
  if (is('dhl ecommerce') || is('dhl global mail') || is('ecommerce')) {
    return `https://www.dhl.com/us-en/home/tracking/tracking-ecommerce.html?submit=1&tracking-id=${encodeURIComponent(tn)}`;
  }
  if (is('ontrac')) {
    return `https://www.ontrac.com/tracking/?number=${encodeURIComponent(tn)}`;
  }
  if (is('amazon') || is('amzl')) {
    return `https://www.17track.net/en/track?nums=${encodeURIComponent(tn)}`;
  }
  if (is('canada post') || is('canadapost')) {
    return `https://www.canadapost-postescanada.ca/track-reperage/en#/search?searchFor=${encodeURIComponent(tn)}`;
  }
  if (is('royal mail')) {
    return `https://www.royalmail.com/track-your-item#/tracking-results/${encodeURIComponent(tn)}`;
  }
  if (is('australia post') || is('auspost') || is('australiapost')) {
    return `https://auspost.com.au/mypost/track/#/details/${encodeURIComponent(tn)}`;
  }
  if (is('evri') || is('hermes')) {
    return `https://www.evri.com/track/parcel/${encodeURIComponent(tn)}`;
  }
  if (is('gls')) {
    return `https://gls-group.com/US/en/parcel-tracking?match=${encodeURIComponent(tn)}`;
  }
  if (is('lasership')) {
    return `https://www.lasership.com/track/${encodeURIComponent(tn)}`;
  }

  // Fallback – 17track multi-carrier
  return `https://www.17track.net/en/track?nums=${encodeURIComponent(tn)}`;
}

export default function Shipments({ eorderId, folId, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [filteredFallback, setFilteredFallback] = useState(false);
  const [lastUrl, setLastUrl] = useState('');

  // selection (for future actions like void/reprint)
  const [selectedIds, setSelectedIds] = useState(new Set());
  const allSelected = rows.length > 0 && selectedIds.size === rows.length;

  // hover preview (cursor-tracking, clamped)
  const [preview, setPreview] = useState({ src: '', x: 0, y: 0, visible: false });
  const previewRef = useRef(null);
  const lastPosRef = useRef({ x: 0, y: 0 });

  const fetchJSON = async (url, opts) => {
    const res = await fetch(url, opts);
    if (!res.ok) {
      let msg = `${res.status} ${res.statusText}`;
      try { const j = await res.json(); if (j?.error) msg = j.error; } catch {}
      throw new Error(msg);
    }
    return res.json();
  };

  useEffect(() => {
    let live = true;
    (async () => {
      if (!eorderId) return;
      setLoading(true);
      setErr('');
      setFilteredFallback(false);
      try {
        // First attempt: respect folId filter (when provided)
        const baseUrl = `${API}/api/eorders/${eorderId}/shipments`;
        const u = new URL(baseUrl, window.location.origin);
        if (folId != null && String(folId).trim() !== '') u.searchParams.set('fulfillment_order_line_id', String(folId));
        const primaryUrl = u.toString();
        setLastUrl(primaryUrl);
        const data1 = await fetchJSON(primaryUrl);
        if (!live) return;
        let rows1 = Array.isArray(data1) ? data1 : [];

        // Fallback: if filtered by folId and nothing returned, load unfiltered list
        if ((folId != null && String(folId).trim() !== '') && rows1.length === 0) {
          const u2 = new URL(baseUrl, window.location.origin);
          const secondaryUrl = u2.toString();
          setLastUrl(secondaryUrl);
          const data2 = await fetchJSON(secondaryUrl);
          rows1 = Array.isArray(data2) ? data2 : [];
          if (rows1.length > 0) setFilteredFallback(true);
        }

        // Attach label image URL
        const mapped = rows1.map(r => ({
          ...r,
          labelImageUrl: r.labelid ? `${API}/api/eorders/${eorderId}/labels/${r.labelid}/image` : null,
        }));
        setRows(mapped);
        setSelectedIds(new Set());
      } catch (e) {
        if (live) { setErr(e.message || 'Failed to load shipments'); setRows([]); }
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => { live = false; };
  }, [eorderId, folId]);

  // selection
  const toggleSelect = (id) => setSelectedIds(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  });
  const toggleSelectAll = () => {
    if (allSelected) setSelectedIds(new Set());
    else setSelectedIds(new Set(rows.map(r => r.id)));
  };

  // toolbar stubs (wire to your backend as you add routes)
  const printPickList  = () => window.open(`${API}/api/eorders/${eorderId}/picklist.pdf`, '_blank');
  const printLabels    = () => window.open(`${API}/api/eorders/${eorderId}/shipping-labels.pdf`, '_blank');
  const printPickLabel = () => window.open(`${API}/api/eorders/${eorderId}/pick-and-ship.pdf`, '_blank');

  const refreshList = async () => {
    setFilteredFallback(false);
    const baseUrl = `${API}/api/eorders/${eorderId}/shipments`;
    const u = new URL(baseUrl, window.location.origin);
    if (folId != null && String(folId).trim() !== '') u.searchParams.set('fulfillment_order_line_id', String(folId));
    const url = u.toString();
    setLastUrl(url);
    const data = await fetchJSON(url);
    const mapped = (Array.isArray(data) ? data : []).map(r => ({
      ...r,
      labelImageUrl: r.labelid ? `${API}/api/eorders/${eorderId}/labels/${r.labelid}/image` : null,
    }));
    setRows(mapped);
    setSelectedIds(new Set());
  };

  const voidShipment = async () => {
    if (!selectedIds.size) return alert('Select at least one shipment to void.');
    const ask = window.confirm(`Void ${selectedIds.size} shipment(s)?`);
    if (!ask) return;

    try {
      for (const id of selectedIds) {
        const res = await fetch(`${API}/api/eorders/${eorderId}/shipments/${id}/void`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' }
        });
        const j = await res.json();
        if (!res.ok || !j.success) throw new Error(j.error || 'Void failed');
      }
      alert('Shipment(s) voided.');
      await refreshList();
    } catch (error) {
      alert(error.message);
    }
  };

  // cursor-tracking preview (image only; for PDFs we open in new tab)
  const placePreview = (clientX, clientY) => {
    const vw = window.innerWidth, vh = window.innerHeight;
    const margin = 12, offset = 16;
    const boxW = previewRef.current?.offsetWidth || Math.min(vw * 0.4, 520);
    const boxH = previewRef.current?.offsetHeight || Math.min(vh * 0.6, 520);
    let left = clientX + offset, top = clientY + offset;
    if (left + boxW + margin > vw) left = clientX - boxW - offset;
    if (top  + boxH + margin > vh) top  = clientY - boxH - offset;
    if (left < margin) left = margin;
    if (top  < margin) top  = margin;
    if (left + boxW + margin > vw) left = vw - boxW - margin;
    if (top  + boxH + margin > vh) top  = vh - boxH - margin;
    setPreview(p => ({ ...p, x: left, y: top }));
  };

  const onEnterPreview = (src, e) => {
    lastPosRef.current = { x: e.clientX, y: e.clientY };
    setPreview({ src, x: e.clientX, y: e.clientY, visible: true });
    requestAnimationFrame(() => placePreview(e.clientX, e.clientY));
  };

  const onMovePreview = (e) => {
    if (!preview.visible) return;
    lastPosRef.current = { x: e.clientX, y: e.clientY };
    placePreview(e.clientX, e.clientY);
  };

  const onLeavePreview = () => setPreview(p => ({ ...p, visible: false }));
  const onPreviewImgLoad = () => {
    if (!preview.visible) return;
    const { x, y } = lastPosRef.current;
    placePreview(x, y);
  };

  return (
    <div className="shipments-root">
      <div className="shipments-toolbar">
        <div className="left">
          <label className="mini-check">
            <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} />
            <span>Select All</span>
          </label>
          {!!selectedIds.size && <span className="sel-count">{selectedIds.size} selected</span>}
        </div>
        <div className="right">
          <button className="btn" onClick={refreshList} title="Refresh shipments">
            <FaSync style={{ marginRight: 6 }} />Refresh
          </button>
          {/* <button className="btn" onClick={printPickList}>Pick List</button>
          <button className="btn" onClick={printLabels}>Labels</button>
          <button className="btn" onClick={printPickLabel}>Pick / Labels</button> */}
          <button className="btn danger" onClick={voidShipment} title="Void selected shipment(s)">
            <FaBan style={{ marginRight: 6 }} />Void
          </button>
          {onClose && (
            <button className="btn" onClick={onClose} title="Close shipments">
              <FaTimes style={{ marginRight: 6 }} />Close
            </button>
          )}
        </div>
      </div>

      {/* <div style={{ fontSize:11, color:'#aaa', margin:'4px 0' }}>{lastUrl}</div> */}

      {loading ? (
        <div className="loading">Loading shipments…</div>
      ) : err ? (
        <div className="loading" style={{ color:'#ff7a7a' }}>{err}</div>
      ) : (
        <div className="shipments-table-wrap">
          {filteredFallback && (
            <div className="notice" style={{ margin:'6px 0 12px', fontSize:12, color:'#888' }}>
              No shipments matched the selected item. Showing <strong>all shipments</strong> for this order.
            </div>
          )}
          <table className="shipments-table">
            <thead>
              <tr>
                <th style={{ width: 40 }}></th>
                <th>Package #</th>
                <th>Carrier</th>
                <th>Tracking #</th>
                <th>Label</th>
                <th>Date / Time</th>
              </tr>
            </thead>
            <tbody>
              {rows.length ? rows.map(r => {
                const isPdf = Number(r.labeltype) === 1;
                const trackingUrl = buildTrackingUrl(r.carrier, r.trackingnumber);

                const buildCacheUrl = (base) => `${base}${base.includes('?') ? '&' : '?'}t=${Date.now()}`;
                const openLabel = () => {
                  if (!r.labelImageUrl) return;
                  window.open(buildCacheUrl(r.labelImageUrl), '_blank', 'noopener,noreferrer');
                };

                return (
                  <tr key={r.id}>
                    <td style={{ textAlign:'center' }}>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(r.id)}
                        onChange={() => toggleSelect(r.id)}
                      />
                    </td>
                    <td>{r.package_number}</td>
                    <td className="nowrap">{(r.carrier || '').toLowerCase()}</td>
                    <td className="nowrap">
                      {r.trackingnumber ? (
                        <a
                          href={trackingUrl || '#'}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={`Open tracking${trackingUrl ? '' : ' (unknown carrier → 17track)'}`}
                          onClick={(e) => { if (!trackingUrl) e.preventDefault(); }}
                        >
                          {r.trackingnumber}
                        </a>
                      ) : '—'}
                    </td>
                    <td style={{ textAlign:'center' }}>
                      {r.labelImageUrl ? (
                        isPdf ? (
                          <a
                            className="btn"
                            href={buildCacheUrl(r.labelImageUrl)}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Open label (PDF)"
                          >
                            PDF Label
                          </a>
                        ) : (
                          <img
                            src={r.labelImageUrl}
                            alt="label"
                            className="ship-thumb"
                            onMouseEnter={(e) => onEnterPreview(r.labelImageUrl, e)}
                            onMouseMove={onMovePreview}
                            onMouseLeave={onLeavePreview}
                            onClick={openLabel}
                            title="Open label"
                          />
                        )
                      ) : (
                        <span style={{ opacity:.5 }}>—</span>
                      )}
                    </td>
                    <td className="nowrap">{r.labeldate ? new Date(r.labeldate).toLocaleString() : ''}</td>
                  </tr>
                );
              }) : (
                <tr><td colSpan={6} style={{ color:'#aaa' }}>No shipments</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Hover Preview (image only) */}
      {preview.visible && preview.src && (
        <div className="ship-preview" ref={previewRef} style={{ left: preview.x, top: preview.y }}>
          <img src={preview.src} alt="" onLoad={onPreviewImgLoad} />
        </div>
      )}
    </div>
  );
}
